import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Calculator, FileInput, FileOutput, CheckCircle2 } from "lucide-react";

const services = [
  {
    icon: BookOpen,
    title: "Bookkeeping",
    price: "From $9.99/hr",
    desc: "Accurate, up-to-date books maintained by dedicated professionals using QuickBooks, Xero, or your preferred platform.",
    features: ["Daily transaction categorization", "Bank & credit card reconciliation", "Monthly financial statements", "Payroll entry & journal adjustments", "Catch-up & clean-up bookkeeping"],
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=900&q=80",
  },
  {
    icon: Calculator,
    title: "Accounting",
    price: "Custom plans",
    desc: "Full-cycle accounting support that keeps your financials audit-ready and your CPA happy at tax time.",
    features: ["Month-end & year-end close", "P&L, balance sheet & cash flow reports", "Budgeting & variance analysis", "US GAAP & Canadian standards", "Tax-season-ready workpapers"],
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&q=80",
  },
  {
    icon: FileInput,
    title: "Accounts Payable",
    price: "FTE at $2,199/mo",
    desc: "A dedicated full-time AP specialist processes, approves, and schedules every vendor payment — on time, every time.",
    features: ["Invoice intake & 3-way matching", "Approval workflow management", "Payment scheduling & processing", "Vendor statement reconciliation", "Early-payment discount capture"],
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&q=80",
  },
  {
    icon: FileOutput,
    title: "Accounts Receivable",
    price: "From $9.99/hr",
    desc: "Get paid faster with professional invoicing, payment tracking, and courteous but persistent follow-up.",
    features: ["Invoice generation & delivery", "Payment posting & tracking", "AR aging reports weekly", "Reminder & collection sequences", "Customer account reconciliation"],
    image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=900&q=80",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A00]">Services</span>
          <h1 className="font-heading font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-4xl">
            Everything your back office needs. <span className="text-[#FF5A00]">Nothing it doesn't.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Four core services for US and Canadian businesses — each backed by our 1-week free trial and transparent pricing.
          </p>
        </div>
      </section>

      {services.map(({ icon: Icon, title, price, desc, features, image }, i) => (
        <section key={title} className="border-b border-border">
          <div className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
            <div className="px-4 sm:px-6 lg:px-8 py-16 lg:py-20 [direction:ltr]" data-testid={`service-section-${title.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 bg-[#FFF3ED] border border-[#FF5A00]/20 flex items-center justify-center">
                  <Icon size={24} className="text-[#FF5A00]" />
                </span>
                <span className="font-mono-price text-sm font-bold text-[#FF5A00]">{price}</span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mt-5">{title}</h2>
              <p className="text-muted-foreground mt-4 leading-relaxed">{desc}</p>
              <ul className="mt-6 space-y-3">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 size={17} className="text-[#FF5A00] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/appointment"
                data-testid={`service-cta-${title.toLowerCase().replace(/\s/g, "-")}`}
                className="inline-flex items-center gap-2 bg-foreground text-white font-bold px-6 py-3.5 mt-8 hover:bg-[#FF5A00] transition-colors"
              >
                Get Started Free <ArrowRight size={16} />
              </Link>
            </div>
            <div className="lg:border-l border-border [direction:ltr] hidden lg:block">
              <img src={image} alt={title} className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      ))}

      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight">Not sure which service fits?</h2>
            <p className="text-zinc-400 mt-2">Book a free consultation — we'll recommend the right plan in 15 minutes.</p>
          </div>
          <Link
            to="/appointment"
            data-testid="services-cta-btn"
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold px-8 py-4 transition-colors whitespace-nowrap"
          >
            Book Free Consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
